import React, { useState, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AuthContext from '../contexts/AuthContext';
// Eliminamos la importación de FcGoogle que causa problemas

const Register: React.FC = () => {
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { registerWithEmail, loginWithGoogle } = useContext(AuthContext);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!displayName || !email || !password || !confirmPassword) {
      setError('Por favor completa todos los campos');
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }
    
    if (password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    
    try {
      setError('');
      setLoading(true);
      await registerWithEmail(email, password, displayName);
      navigate('/');
    } catch (err: any) {
      console.error(err);
      setError('Error al registrarse: ' + (err.message || 'Inténtalo de nuevo'));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      setError('');
      setLoading(true);
      // Si estamos dentro de la app Android, usar el puente nativo
      const android = (window as any).Android;
      if (android && typeof android.startGoogleSignIn === 'function') {
        console.log('Android bridge detectado, delegando login a la app nativa (Register)...');
        android.startGoogleSignIn();
        // La app nativa gestionará el flujo y el estado de autenticación
        return;
      }

      await loginWithGoogle();
      navigate('/');
    } catch (err: any) {
      console.error(err);
      setError('Error al iniciar sesión con Google: ' + (err.message || 'Inténtalo de nuevo'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto">
      <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg p-6">
        <h2 className="text-2xl font-bold text-center mb-6 text-domired-600">Crear una Cuenta</h2>
        
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4" role="alert">
            <span className="block sm:inline">{error}</span>
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="displayName" className="block text-sm font-medium mb-1">
              Nombre de Usuario
            </label>
            <input
              type="text"
              id="displayName"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-domired-500 focus:border-domired-500 dark:bg-gray-700"
              required
            />
          </div>
          
          <div>
            <label htmlFor="email" className="block text-sm font-medium mb-1">
              Correo Electrónico
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-domired-500 focus:border-domired-500 dark:bg-gray-700"
              required
            />
          </div>
          
          <div>
            <label htmlFor="password" className="block text-sm font-medium mb-1">
              Contraseña
            </label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-domired-500 focus:border-domired-500 dark:bg-gray-700"
              required
            />
          </div>
          
          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium mb-1">
              Confirmar Contraseña
            </label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-domired-500 focus:border-domired-500 dark:bg-gray-700"
              required
            />
          </div>
          
          <div>
            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-domired-600 hover:bg-domired-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-domired-500 disabled:opacity-50"
            >
              {loading ? 'Registrando...' : 'Registrarse'}
            </button>
          </div>
        </form>
        
        <div className="mt-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white dark:bg-gray-800 text-gray-500">
                O continúa con
              </span>
            </div>
          </div>
          
          <div className="mt-6">
            <button
              onClick={handleGoogleLogin}
              disabled={loading}
              className="w-full flex justify-center items-center py-2 px-4 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-domired-500"
            >
              <span className="flex items-center">
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z" fill="#4285F4"/>
                  <path d="M6.52 7.013l-2.88-2.08A12 12 0 0 1 12.48 0C16.133 0 18.747 1.44 20.667 3.36l-2.307 2.307C16.96 4.347 15.053 3.32 12.453 3.32c-3.16 0-5.84 1.733-7.253 4.36l-2.68-1.667C4.96 3.2 8.333 0 12.48 0c3.573 0 6.267 1.173 8.373 3.36L18.4 5.76c-1.4-1.32-3.307-2.347-5.907-2.347-3.307 0-6.067 2.32-7.013 5.4l-1.96-.8z" fill="#EA4335"/>
                  <path d="M12.48 24c-6.613 0-12-5.387-12-12s5.387-12 12-12c3.653 0 6.267 1.44 8.187 3.36l-2.307 2.307C16.96 4.347 15.053 3.32 12.453 3.32c-4.827 0-8.6 3.893-8.6 8.72s3.773 8.72 8.6 8.72c3.12 0 4.907-1.253 6.053-2.4.934-.947 1.547-2.293 1.787-4.133h-7.84v-3.28h11.32c.12.586.173 1.293.173 2.053 0 2.453-.68 5.507-2.84 7.667-2.106 2.187-4.8 3.36-8.373 3.36z" fill="#34A853"/>
                </svg>
                Google
              </span>
            </button>
          </div>
        </div>
        
        <p className="mt-4 text-center text-sm text-gray-600 dark:text-gray-400">
          ¿Ya tienes una cuenta?{' '}
          <Link to="/login" className="font-medium text-domired-600 hover:text-domired-500">
            Iniciar Sesión
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Register;
